import { useState } from "react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText, Eye, User, Clock, AlertTriangle, DollarSign, Calendar, Timer } from "lucide-react";
import { ManualReviewModal } from "./ManualReviewModal";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { format, differenceInHours, differenceInDays, addHours } from "date-fns";

export function ManualReviewQueue() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const [selectedBill, setSelectedBill] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: bills = [], refetch } = useQuery({
    queryKey: ['manual-review-queue', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      
      const { data, error } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('processing_status', 'review_required' as any)
        .eq('user_id', user?.id)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id && !!user?.id
  });

  const handleClaimAndReview = async (bill: any) => {
    try {
      // Claim the bill for current user
      const { error } = await supabase
        .from('bill_upload_queue')
        .update({
          claimed_by: user?.id,
          claimed_at: new Date().toISOString()
        } as any)
        .eq('id', bill.id);

      if (error) throw error;

      toast({
        title: "Bill Claimed",
        description: `Bill ${bill.bill_number || bill.id.substring(0, 8)} has been assigned to you`
      });

      // Open review modal
      setSelectedBill({ ...bill, claimed_by: user?.id, claimed_at: new Date().toISOString() });
      setIsModalOpen(true);
      refetch();
    } catch (error: any) {
      toast({
        title: "Failed to claim bill",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Enhanced priority calculation with SLA, amount, and due date
  const calculatePriorityDetails = (bill: any) => {
    const reasons: string[] = [];
    let calculatedPriority = bill.priority || 50;
    
    // SLA breach check
    const slaDueAt = bill.sla_due_at ? new Date(bill.sla_due_at) : null;
    const now = new Date();
    const isSlaBreach = slaDueAt && slaDueAt < now;
    const hoursUntilSla = slaDueAt ? differenceInHours(slaDueAt, now) : null;
    
    if (isSlaBreach) {
      reasons.push('🔴 SLA Breached');
      calculatedPriority = Math.max(calculatedPriority, 90);
    } else if (hoursUntilSla !== null && hoursUntilSla < 4) {
      reasons.push('⚠️ SLA Due Soon');
      calculatedPriority = Math.max(calculatedPriority, 80);
    }
    
    // High amount check (>$1,000)
    if (bill.amount && bill.amount > 1000) {
      reasons.push('💰 High Amount');
      calculatedPriority = Math.max(calculatedPriority, 75);
    }
    
    // Due date check (<3 days)
    const billDueDate = bill.bill_date ? new Date(bill.bill_date) : null;
    if (billDueDate) {
      const daysUntilDue = differenceInDays(billDueDate, now);
      if (daysUntilDue < 3 && daysUntilDue >= 0) {
        reasons.push('📅 Due Soon');
        calculatedPriority = Math.max(calculatedPriority, 80);
      } else if (daysUntilDue < 0) {
        reasons.push('🚨 Overdue');
        calculatedPriority = Math.max(calculatedPriority, 95);
      }
    }
    
    // Low confidence check
    if (bill.ai_confidence && bill.ai_confidence < 0.7) {
      reasons.push('📉 Low Confidence');
    } else if (bill.ai_confidence && bill.ai_confidence < 0.85) {
      reasons.push('⚡ Medium Confidence');
    }
    
    return { priority: calculatedPriority, reasons, isSlaBreach, hoursUntilSla };
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 80) return 'bg-red-100 dark:bg-red-950/20 text-red-800 dark:text-red-400 border-red-500';
    if (priority >= 50) return 'bg-yellow-100 dark:bg-yellow-950/20 text-yellow-800 dark:text-yellow-400 border-yellow-500';
    return 'bg-green-100 dark:bg-green-950/20 text-green-800 dark:text-green-400 border-green-500';
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.95) return 'text-green-600';
    if (confidence >= 0.85) return 'text-yellow-600';
    return 'text-red-600';
  };

  // SLA Timer Component
  const SlaTimer = ({ slaDueAt }: { slaDueAt: string | null }) => {
    if (!slaDueAt) return null;
    
    const dueDate = new Date(slaDueAt);
    const now = new Date();
    const hoursRemaining = differenceInHours(dueDate, now);
    const isOverdue = hoursRemaining < 0;
    
    const getTimerColor = () => {
      if (isOverdue) return 'text-red-500 bg-red-50 dark:bg-red-950/30';
      if (hoursRemaining < 4) return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950/30';
      return 'text-green-600 bg-green-50 dark:bg-green-950/30';
    };

    return (
      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getTimerColor()}`}>
        <Timer className="h-3 w-3" />
        {isOverdue ? (
          <span>{Math.abs(hoursRemaining)}h overdue</span>
        ) : (
          <span>{hoursRemaining}h remaining</span>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Bills Awaiting Review</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline" className="bg-red-50 dark:bg-red-950/20">
                <span className="h-2 w-2 rounded-full bg-red-500 mr-2" />
                High Priority: {bills.filter(b => b.priority >= 80).length}
              </Badge>
              <Badge variant="outline" className="bg-yellow-50 dark:bg-yellow-950/20">
                <span className="h-2 w-2 rounded-full bg-yellow-500 mr-2" />
                Medium: {bills.filter(b => b.priority >= 50 && b.priority < 80).length}
              </Badge>
              <Badge variant="outline" className="bg-green-50 dark:bg-green-950/20">
                <span className="h-2 w-2 rounded-full bg-green-500 mr-2" />
                Low: {bills.filter(b => b.priority < 50).length}
              </Badge>
            </div>
          </div>

          {bills.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No bills awaiting review
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>SLA</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bills.map((bill) => {
                  const priorityDetails = calculatePriorityDetails(bill);
                  
                  return (
                  <TableRow key={bill.id} className={`hover:bg-muted/50 ${priorityDetails.isSlaBreach ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}>
                    <TableCell>
                      <FileText className="h-5 w-5 text-blue-500" />
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="space-y-1">
                              <Badge 
                                variant="outline" 
                                className={`border-2 ${getPriorityColor(priorityDetails.priority)}`}
                              >
                                {priorityDetails.priority >= 80 ? 'HIGH' : priorityDetails.priority >= 50 ? 'MEDIUM' : 'LOW'}
                              </Badge>
                              {priorityDetails.reasons.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {priorityDetails.reasons.slice(0, 2).map((reason, idx) => (
                                    <span key={idx} className="text-xs text-muted-foreground">
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-1">
                              <p className="font-medium">Priority Factors:</p>
                              {priorityDetails.reasons.map((reason, idx) => (
                                <p key={idx}>{reason}</p>
                              ))}
                              {priorityDetails.reasons.length === 0 && <p>Standard priority</p>}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell className="font-medium">{bill.file_name}</TableCell>
                    <TableCell>{bill.supplier_name || '—'}</TableCell>
                    <TableCell className="font-medium">
                      {bill.amount ? `$${bill.amount.toFixed(2)}` : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${getConfidenceColor(bill.ai_confidence || 0)}`}>
                          {Math.round((bill.ai_confidence || 0) * 100)}%
                        </span>
                        {(bill.ai_confidence || 0) < 0.85 && (
                          <span className="text-xs text-muted-foreground">Low</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {bill.claimed_by ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {bill.claimed_by.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-sm">
                            <div className="font-medium">In Review</div>
                            {bill.claimed_at && (
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(bill.claimed_at), 'h:mm a')}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <Badge variant="secondary">
                          <Clock className="h-3 w-3 mr-1" />
                          Awaiting Claim
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <SlaTimer slaDueAt={bill.sla_due_at} />
                    </TableCell>
                    <TableCell>
                      {!bill.claimed_by || bill.claimed_by === user?.id ? (
                        <Button 
                          size="sm"
                          onClick={() => handleClaimAndReview(bill)}
                          className="gap-2"
                        >
                          <User className="h-4 w-4" />
                          {bill.claimed_by ? 'Continue Review' : 'Claim & Review'}
                        </Button>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedBill(bill);
                            setIsModalOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Only
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      {selectedBill && (
        <ManualReviewModal
          bill={selectedBill}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedBill(null);
            refetch();
          }}
          onSave={() => {
            setIsModalOpen(false);
            setSelectedBill(null);
            refetch();
          }}
        />
      )}
    </>
  );
}
